import React, { useState } from 'react';
import { Send, Check, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import StarField from './StarField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.042zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
  </svg>
);

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    inquiry_type: 'Custom Build',
    inquiry: '',
    website: '', // honeypot field
  });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({
        type: 'success',
        message: "Your message has been sent! I'll get back to you soon.",
      });
      setFormData({ name: '', email: '', whatsapp: '', inquiry_type: 'Custom Build', inquiry: '', website: '' });
    } catch (error) {
      const status = error.response?.status;
      let message = error.response?.data?.detail || 'Failed to send message. Please try again.';
      if (status === 429) {
        message = 'Too many submissions. Please try again in an hour.';
      }
      setStatus({
        type: 'error',
        message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="min-h-screen pt-32 pb-24 bg-black relative overflow-hidden">
      <StarField density="sparse" />
      <div className="container mx-auto px-6 max-w-2xl relative z-10">
        {/* Contact info */}
        <div className="mb-12 space-y-4">
          <a
            href="mailto:info@chilltimeandwatch.com"
            className="flex items-center justify-center space-x-3 text-white font-light text-base md:text-lg hover:opacity-80 transition-opacity"
          >
            <Mail className="w-6 h-6 text-white" />
            <span>info@chilltimeandwatch.com</span>
          </a>
          <a
            href="https://wa.me/16472290114"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center space-x-3 text-white font-light text-base md:text-lg hover:opacity-80 transition-opacity"
          >
            <WhatsAppIcon className="w-6 h-6 text-white" />
            <span>+1 (647) 229-0114</span>
          </a>
        </div>

        {/* Contact Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-10 space-y-5"
        >
          {/* Honeypot field — invisible to humans, bots fill it out */}
          <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
            <label htmlFor="website">Website (leave this empty)</label>
            <input
              id="website"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website}
              onChange={handleChange}
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-xs text-gray-400 font-light uppercase tracking-widest mb-2">
              Name
            </label>
            <input
              id="name"
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              className="w-full bg-black/30 border border-white/10 px-4 py-3 text-white placeholder-gray-600 font-light focus:outline-none focus:border-white/40 focus:bg-black/50 transition-all duration-300"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-xs text-gray-400 font-light uppercase tracking-widest mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="w-full bg-black/30 border border-white/10 px-4 py-3 text-white placeholder-gray-600 font-light focus:outline-none focus:border-white/40 focus:bg-black/50 transition-all duration-300"
            />
          </div>

          <div>
            <label htmlFor="whatsapp" className="block text-xs text-gray-400 font-light uppercase tracking-widest mb-2">
              WhatsApp Number <span className="text-gray-600 normal-case">(Optional)</span>
            </label>
            <input
              id="whatsapp"
              type="tel"
              name="whatsapp"
              value={formData.whatsapp}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
              className="w-full bg-black/30 border border-white/10 px-4 py-3 text-white placeholder-gray-600 font-light focus:outline-none focus:border-white/40 focus:bg-black/50 transition-all duration-300"
            />
          </div>

          <div>
            <label htmlFor="inquiry_type" className="block text-xs text-gray-400 font-light uppercase tracking-widest mb-2">
              Inquiry Type
            </label>
            <select
              id="inquiry_type"
              name="inquiry_type"
              required
              value={formData.inquiry_type}
              onChange={handleChange}
              className="w-full bg-black/30 border border-white/10 px-4 py-3 text-white font-light focus:outline-none focus:border-white/40 focus:bg-black/50 transition-all duration-300 appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                paddingRight: '2.5rem',
              }}
            >
              <option value="Custom Build" className="bg-black">Custom Build</option>
              <option value="Workshop" className="bg-black">Workshop</option>
              <option value="Question" className="bg-black">Question</option>
              <option value="Other" className="bg-black">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="inquiry" className="block text-xs text-gray-400 font-light uppercase tracking-widest mb-2">
              Inquiry
            </label>
            <textarea
              id="inquiry"
              name="inquiry"
              required
              rows={6}
              value={formData.inquiry}
              onChange={handleChange}
              placeholder="Tell me about your custom build project, ideas, or questions..."
              className="w-full bg-black/30 border border-white/10 px-4 py-3 text-white placeholder-gray-600 font-light focus:outline-none focus:border-white/40 focus:bg-black/50 transition-all duration-300 resize-none"
            />
          </div>

          {/* Status Messages */}
          {status.message && (
            <div
              className={`flex items-start space-x-3 px-4 py-3 border ${
                status.type === 'success'
                  ? 'bg-green-500/10 border-green-500/30 text-green-300'
                  : 'bg-red-500/10 border-red-500/30 text-red-300'
              }`}
            >
              {status.type === 'success' ? (
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <p className="text-sm font-light">{status.message}</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-white text-black hover:bg-gray-200 font-light py-6 text-base transition-all duration-300 hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
